// The WebSocket: a ticket from the API, events dispatched to the state, reconnection after
// 1, 2, 5, 10 then 30 seconds, and the `active` / `inactive` / `typing` signals that decide
// presence and, later, push notifications (docs/spec.md, Temps réel).
import { api, type Conversation, type Message, type Reaction } from './api';
import { app, type Presence } from './stores.svelte';

const BACKOFF_S = [1, 2, 5, 10, 30];
const ACTIVE_EVERY_MS = 20_000;
const TYPING_EVERY_MS = 3_000;

type Event =
  | { type: 'hello'; data: { user_id: string; server_time: string; presence: Record<string, Presence> } }
  | { type: 'message.created' | 'message.updated'; data: Message }
  | { type: 'message.deleted'; data: { id: string; conversation_id: string; root_id: string | null } }
  | { type: 'reaction.changed'; data: { message_id: string; conversation_id: string; root_id: string | null; reactions: Reaction[] } }
  | { type: 'saved.changed'; data: { message_id: string; conversation_id: string; saved: boolean } }
  | { type: 'conversation.upserted'; data: Conversation }
  | { type: 'conversation.removed'; data: { id: string } }
  | { type: 'read.updated'; data: { conversation_id: string; last_read_at: string } }
  | { type: 'typing'; data: { conversation_id: string; user_id: string } }
  | { type: 'presence'; data: { user_id: string; status: Presence } }
  | { type: 'task.upserted'; data: Message }
  | { type: 'user.updated'; data: { id: string; username: string; display_name: string; avatar_url: string | null } };

let socket: WebSocket | null = null;
let attempt = 0;
let retry: ReturnType<typeof setTimeout> | undefined;
let heartbeat: ReturnType<typeof setInterval> | undefined;
let stopped = true;
let lastTyping = 0;
let hasConnected = false;

/** Called when the connection drops and comes back. */
let onConversationRemoved: (id: string) => void = () => {};
export function onRemoved(handler: (id: string) => void): void {
  onConversationRemoved = handler;
}

function send(message: object): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

/** Tells the server what is on screen: called on focus, blur and conversation change. */
export function signalActivity(): void {
  if (app.focused && document.visibilityState === 'visible') {
    send({ type: 'active', conversation_id: app.openId });
  } else {
    send({ type: 'inactive' });
  }
}

export function signalTyping(conversationId: string): void {
  const now = Date.now();
  if (now - lastTyping < TYPING_EVERY_MS) return;
  lastTyping = now;
  send({ type: 'typing', conversation_id: conversationId });
}

function dispatch(event: Event): void {
  switch (event.type) {
    case 'hello':
      app.presence = event.data.presence;
      break;
    case 'message.created':
      app.addMessage(event.data);
      break;
    case 'message.updated':
      app.updateMessage(event.data);
      break;
    case 'message.deleted':
      app.deleteMessage(event.data.id);
      break;
    case 'reaction.changed':
      app.setReactions(event.data.message_id, event.data.reactions);
      break;
    case 'saved.changed':
      app.setSaved(event.data.message_id, event.data.saved);
      break;
    case 'conversation.upserted':
      app.upsertConversation(event.data);
      break;
    case 'task.upserted':
      app.upsertTask(event.data);
      break;
    case 'user.updated':
      app.applyUser(event.data);
      break;
    case 'conversation.removed':
      app.removeConversation(event.data.id);
      onConversationRemoved(event.data.id);
      break;
    case 'read.updated':
      void app.readElsewhere(event.data.conversation_id).catch(() => {});
      break;
    case 'typing':
      app.startTyping(event.data.conversation_id, event.data.user_id);
      break;
    case 'presence':
      if (event.data.status === 'offline') {
        const { [event.data.user_id]: _gone, ...rest } = app.presence;
        app.presence = rest;
      } else {
        app.presence = { ...app.presence, [event.data.user_id]: event.data.status };
      }
      break;
  }
}

async function open(): Promise<void> {
  if (stopped) return;
  let ticket: string;
  try {
    ticket = (await api.wsTicket()).ticket;
  } catch {
    schedule();
    return;
  }
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = new WebSocket(`${scheme}://${location.host}/api/ws?ticket=${encodeURIComponent(ticket)}`);
  socket = ws;

  ws.onopen = () => {
    attempt = 0;
    app.connected = true;
    signalActivity();
    // After a cut, reload what may have changed meanwhile.
    if (hasConnected) {
      void app.loadConversations().catch(() => {});
      if (app.openId) void app.catchUp(app.openId).catch(() => {});
    }
    hasConnected = true;
  };
  ws.onmessage = (message) => {
    try {
      dispatch(JSON.parse(String(message.data)) as Event);
    } catch {
      // An event this version does not understand: ignored.
    }
  };
  ws.onclose = () => {
    if (socket === ws) socket = null;
    app.connected = false;
    schedule();
  };
}

function schedule(): void {
  if (stopped) return;
  const delay = BACKOFF_S[Math.min(attempt, BACKOFF_S.length - 1)]! * 1000;
  attempt += 1;
  clearTimeout(retry);
  retry = setTimeout(() => void open(), delay);
}

function onFocusChange(): void {
  app.focused = document.hasFocus() && document.visibilityState === 'visible';
  signalActivity();
}

export function connect(): void {
  if (!stopped) return;
  stopped = false;
  hasConnected = false;
  void open();
  heartbeat = setInterval(() => {
    if (app.focused) signalActivity();
  }, ACTIVE_EVERY_MS);
  addEventListener('focus', onFocusChange);
  addEventListener('blur', onFocusChange);
  document.addEventListener('visibilitychange', onFocusChange);
}

export function disconnect(): void {
  stopped = true;
  clearTimeout(retry);
  clearInterval(heartbeat);
  removeEventListener('focus', onFocusChange);
  removeEventListener('blur', onFocusChange);
  document.removeEventListener('visibilitychange', onFocusChange);
  socket?.close();
  socket = null;
  app.connected = false;
}
